import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, Inject, Input, LOCALE_ID, OnDestroy, Output, ViewChild } from '@angular/core';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import {
    MatLegacyPaginator as MatPaginator,
    MatLegacyPaginatorIntl as MatPaginatorIntl,
    MatLegacyPaginatorModule as MatPaginatorModule,
} from '@angular/material/legacy-paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatLegacyTable as MatTable, MatLegacyTableModule as MatTableModule } from '@angular/material/legacy-table';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { State } from '../../../dave-data-module/State';
import { LengthPipe } from '../../../helper/length.pipe';
import { LedgerImportSelectTableDataSource, LedgerImportSelectTableItem } from './ledger-import-select-table-datasource';
import { compareArrays } from '../../../helper/helper';
import {
    LedgerImportDocumentTypeNames,
    LedgerImportDocumentTypes,
    ledgerImportDocumentTypesValues, ledgerImportTableColumnNames,
} from '../../../dave-data-module/entities/ledger-import.entity';
import { MatLegacyProgressBarModule } from '@angular/material/legacy-progress-bar';
import { GermanPaginatorIntl } from '../../../services/german-paginator-intl.service';
import { MatBadgeModule } from '@angular/material/badge';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { AppButtonModule } from '../../../dave-utils-module/app-button-module/app-button.module';
import { AppFilterModule } from '../../../dave-utils-module/app-filter-module/app-filter.module';
import {
    FILTER_TYPE_SEARCH_MULTI_SELECT,
    FilterOption,
    IFilterTypeSearchMultiSelectValue,
} from '../../../dave-utils-module/app-filter-module/app-filter/app-filter.component';
import { FilterTypes } from '../../../services/default-filter.service';
export interface LedgerImportSelectTableFilterValues {
    [FilterTypes.LedgerImportDocTypes]: IFilterTypeSearchMultiSelectValue<LedgerImportDocumentTypes>[];
}
export type LedgerImportSelectTableComponentLedgerImportFilter = {
    customerIds: number[],
    commissionIds: number[],
    documentTypes: LedgerImportDocumentTypes[] | null,
};
@Component({
    selector: 'app-ledger-import-select-table',
    templateUrl: './ledger-import-select-table.component.html',
    styleUrls: ['./ledger-import-select-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        MatTableModule,
        MatSortModule,
        MatPaginatorModule,
        MatCheckboxModule,
        MatLegacyProgressBarModule,
        AppFilterModule,
        AppButtonModule,
        FontAwesomeModule,
        MatBadgeModule,
        MatTooltipModule,
    ],
    providers: [{ provide: MatPaginatorIntl, useClass: GermanPaginatorIntl }],
})
export class LedgerImportSelectTableComponent implements AfterViewInit, OnDestroy {
    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;
    @ViewChild(MatTable) table!: MatTable<LedgerImportSelectTableItem>;
    dataSource: LedgerImportSelectTableDataSource;

    /** Columns displayed in the table. Columns IDs can be added, removed, or reordered. */
    displayedColumns: Array<keyof LedgerImportSelectTableItem | 'checkbox'> = ['checkbox', 'ConsolidatedInvoiceId', 'ConsolidatedDate', 'CreatedAt', 'DueDate', 'ConsolidatedAmountWithoutTax', 'notes', 'Type'];
    autoDisplayedColumns: Array<keyof LedgerImportSelectTableItem> = ['ConsolidatedInvoiceId', 'ConsolidatedDate', 'CreatedAt', 'ConsolidatedAmount', 'ConsolidatedAmountWithoutTax', 'notes', 'DueDate', 'Type'];
    columnHeaders: { [key in keyof LedgerImportSelectTableItem]: string } = {
        ConsolidatedInvoiceId: ledgerImportTableColumnNames.ConsolidatedInvoiceId,
        ConsolidatedAmount: ledgerImportTableColumnNames.ConsolidatedAmount,
        ConsolidatedAmountWithoutTax: ledgerImportTableColumnNames.ConsolidatedNettoAmount,
        notes: ledgerImportTableColumnNames.notes,
        DueDate: ledgerImportTableColumnNames.DueDate,
        Type: ledgerImportTableColumnNames.Type,
        ConsolidatedDate: ledgerImportTableColumnNames.ConsolidatedDate,
        CreatedAt: ledgerImportTableColumnNames.CreatedAt,
        CommissionId: '',
        isSelected$: '',
        Id: '',
        sortType: '',
        sortCreatedAtTime: '',
        sortConsolidatedDate: '',
        sortDueDate: '',
        sortConsolidatedAmount: '',
        sortConsolidatedAmountWithoutTax: '',
    };
    protected emptyText = 'keine Einträge vorhanden';
    protected multiple$ = new BehaviorSubject<boolean>(false);
    protected disableUncheckedCheckboxes$: Observable<boolean>;
    protected filterValues$ = new BehaviorSubject<LedgerImportSelectTableFilterValues>({ [FilterTypes.LedgerImportDocTypes]: [] });
    protected filterAmount$ = this.filterValues$.pipe(map((f) => (f[FilterTypes.LedgerImportDocTypes]?.length ? 1 : 0)));
    protected filterSettings$: Observable<FilterOption[]>;
    /** filtering makes no sense if the parent only allows one document type */
    protected showFilter$: Observable<boolean>;
    // private selectableLedgerImportIds$ = new BehaviorSubject<number[]>([]);


    @Input() set ledgerImportFilter(filter: LedgerImportSelectTableComponentLedgerImportFilter) {
        this.dataSource.filter$.next(filter);
        // remove selected document types which are no longer allowed by the parent
        const selectedDocTypes = this.filterValues$.value[FilterTypes.LedgerImportDocTypes];
        if (filter?.documentTypes?.length && selectedDocTypes.some((v) => !filter.documentTypes.includes(v.id))) {
            this.setFilterValues({ [FilterTypes.LedgerImportDocTypes]: selectedDocTypes.filter((v) => filter.documentTypes.includes(v.id)) });
        }
    }

    // @Input() set selectableLedgerImportIds(ids: number[]) {
    //     if (JSON.stringify(ids) !== JSON.stringify(this.selectableLedgerImportIds$.value)) {
    //         this.selectableLedgerImportIds$.next([...ids]);
    //     }
    // }
    @Input() set selectedLedgerImports(v:number[]) {
        if (!compareArrays(this.dataSource.selectedLedgerImports$.value, v)) {
            this.dataSource.selectedLedgerImports$.next([...v]);
        }
    }
    @Output() selectedLedgerImportsChange = new EventEmitter<number[]>();

    @Input()
    set multiple(value: boolean) {
        this.multiple$.next(coerceBooleanProperty(value));
    }
    get multiple() {
        return this.multiple$.value;
    }
    // tslint:disable-next-line: member-ordering naming-convention
    static ngAcceptInputType_multiple: BooleanInput;
    @Input()
    set hideCheckbox(value: boolean) {
        if (coerceBooleanProperty(value)) {
            this.displayedColumns = this.displayedColumns.filter(c => c !== 'checkbox');
        }
        // todo checkbox hinzufügen wenn false
    }
    @Input() set defaultDocumentTypesFilter(values: LedgerImportDocumentTypes[]) {
        this.setFilterValues({...this.filterValues$.value, [FilterTypes.LedgerImportDocTypes]: values.map<IFilterTypeSearchMultiSelectValue<LedgerImportDocumentTypes>>(v => ({id: v, label: LedgerImportDocumentTypeNames.get(v)}))});
    }
    // tslint:disable-next-line: member-ordering naming-convention
    static ngAcceptInputType_hideCheckbox: BooleanInput;
    changeSubscription: Subscription | undefined;
    constructor(store: Store<State>, @Inject(LOCALE_ID) locale: string) {
        this.dataSource = new LedgerImportSelectTableDataSource(locale);
        this.changeSubscription = this.dataSource.selectedLedgerImports$.subscribe((val) => this.selectedLedgerImportsChange.emit([...val]));
        this.disableUncheckedCheckboxes$ = combineLatest([this.multiple$, this.dataSource.selectedLedgerImports$]).pipe(
            map(([multi, selected]) => !multi && selected.length > 0),
            distinctUntilChanged(),
            shareReplay({ refCount: true, bufferSize: 1 }),
        );
        const availableDocumentTypes$ = this.dataSource.filter$.pipe(
            map((filter) => (filter?.documentTypes?.length ? filter.documentTypes : ledgerImportDocumentTypesValues)),
            shareReplay({ refCount: true, bufferSize: 1 }),
        );
        this.showFilter$ = availableDocumentTypes$.pipe(map((types) => types.length > 1));
        this.filterSettings$ = availableDocumentTypes$.pipe(
            map((types) => [
                {
                    Name: FilterTypes.LedgerImportDocTypes,
                    Type: FILTER_TYPE_SEARCH_MULTI_SELECT,
                    Label: 'Typ',
                    Icon: 'filter',
                    Values: types.map<IFilterTypeSearchMultiSelectValue<LedgerImportDocumentTypes>>((t) => ({ id: t, label: LedgerImportDocumentTypeNames.get(t) })),
                },
            ]),
        );
    }
    setFilterValues(values: LedgerImportSelectTableFilterValues) {
        this.filterValues$.next(values);
        this.dataSource.documentTypeFilter$.next((values[FilterTypes.LedgerImportDocTypes] || []).map((v) => v.id));
    }

    ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
        this.table.dataSource = this.dataSource;
    }
    toggleSelection(liId: number) {
        const isSelected = this.dataSource.selectedLedgerImports$.value.includes(liId);
        if (isSelected) {
            this.dataSource.selectedLedgerImports$.next(this.dataSource.selectedLedgerImports$.value.filter((id) => id !== liId));
        } else if (this.multiple$.value) {
            this.dataSource.selectedLedgerImports$.next([...this.dataSource.selectedLedgerImports$.value, liId]);
        } else {
            this.dataSource.selectedLedgerImports$.next([liId]);
        }
    }
    selectAll(select: boolean) {
        if (select) {
            firstValueFrom(this.dataSource.data$).then(data => {
                this.dataSource.selectedLedgerImports$.next(data.map(d => d.Id));
            })
        } else {
            this.dataSource.selectedLedgerImports$.next([]);
        }
    }
    ngOnDestroy(): void {
        this.changeSubscription?.unsubscribe();
    }
}
